import { useCallback, useMemo } from "react";
import {
  useTicketTemplateUpdateMutation,
  useTicketTemplateQuery,
  TICKET_TEMPLATES_QUERY,
} from "@ticketingplatform/api/dist/ticketTemplate";
import { Form, message } from "antd";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useIntl } from "react-intl";
import { useNumberParams } from "hooks/useNumberParams";
import pick from "lodash/pick";
import usePrompt from "hooks/usePrompt";
import { ROUTE_PATHS } from "const/paths";
import { useNavigate } from "react-router-dom";

import { TicketTemplateFormValues } from "../TicketTemplateCreate/partials/TicketTemplateForm/types";
import { initialValues } from "../TicketTemplateCreate/consts";

import {
  UseTicketTemplateEdit,
  UseTicketTemplateEditReturnType,
} from "./types";

export const useTicketTemplateEdit: UseTicketTemplateEdit = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [form] = Form.useForm<TicketTemplateFormValues>();
  const { id } = useNumberParams(true);

  const { loading: readLoading } = useTicketTemplateQuery({
    variables: { id },
    onCompleted(data) {
      const { ticketTemplate } = data || {};

      if (!ticketTemplate) {
        return;
      }

      form.setFieldsValue({
        input: {
          name: ticketTemplate.name,
          ticketPools: ticketTemplate.ticketPools?.nodes.map(
            ({ hasAnyTicketSold, ...ticketPool }) => ({
              ...ticketPool,
              hasAnyTicketSold: !!hasAnyTicketSold,
            })
          ),
        },
      });
    },
  });

  const [
    ticketTemplateUpdateMutation,
    { loading: updateLoading, error: updateError },
  ] = useTicketTemplateUpdateMutation({
    evictQueries: [TICKET_TEMPLATES_QUERY],
  });

  const fields = useApolloErrorFields(updateError);

  usePrompt(updateLoading);

  const onFinish = useCallback(
    async ({ input: { name, ticketPools } }: TicketTemplateFormValues) => {
      try {
        await ticketTemplateUpdateMutation({
          variables: {
            id,
            input: {
              name,
              ticketPools: ticketPools?.map((ticketPool) =>
                pick(
                  ticketPool,
                  "name",
                  "price",
                  "quantity",
                  "venueFee",
                  "description",
                  "availableBeforeDays",
                  "availableBeforeTime",
                  "expireBeforeDays",
                  "expireBeforeTime"
                )
              ),
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Ticket Template has been updated!",
          })
        );

        navigate(ROUTE_PATHS.TicketTemplateList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Error. Cannot add Ticket Template.",
          })
        );
      }
    },
    [ticketTemplateUpdateMutation, id, intl, navigate]
  );

  return useMemo<UseTicketTemplateEditReturnType>(
    () => ({
      form: {
        initialValues,
        loading: readLoading || updateLoading,
        fields,
        form,
        onFinish,
      },
    }),
    [fields, form, onFinish, readLoading, updateLoading]
  );
};
